/**
 * 가입 폼 선택②(세그먼트) 선택지·라벨 SSoT — 설계 `10-회원정보-수집체계-재설계.md` §3.2·§4.2.
 *
 * 백엔드는 영문 코드만 알고 **한글 라벨은 프론트가 소유**한다(`ipsiCardOptions.ts` 와 동일 경계) —
 * 라벨 문구를 바꿔도 DB 마이그레이션이 필요 없게 하기 위해서다.
 *
 * ⚠️ 값(value)은 백엔드 enum 과 1:1 계약이다. 추가·변경 시 함께 바뀌어야 한다:
 *   - memberRole   → Java enum MemberRole   (+ member_profile.member_role 컬럼 코멘트)
 *   - studentGrade → Java enum StudentGrade (+ member_profile.student_grade 컬럼 코멘트)
 *   - region       → core.sys_dict_detail(review_author_region) 17종·ipsi.highschool.sido
 *
 * ⚠️ **후기 작성자 유형 13종과 다른 집합이다.** 후기 Dict 는 역할·학년을 한 값에 합쳐
 * (`STUDENT_H3`) 시드했고, 여기는 학년을 분리한 정규화 원본이다. 후기 조합값은
 * 작성 시점에 두 값을 합쳐 만든다(설계 §5.2).
 */

import type { CardOption } from '#/data/analysis/ipsiCardOptions';

/**
 * 가입 폼에 노출하는 이용자 유형 — 전부 선택 입력(필수 아님).
 *
 * ⚠️ 백엔드 `MemberRole` enum 7종 중 **`CONSULTANT`(입시 컨설턴트)는 의도적으로 제외**한다
 * (2026-08-13 결정). 경쟁 사업자가 자기 신분을 밝히고 가입할 이유가 없어 실효가 없고,
 * 선택지에 두면 서비스가 동종업자를 고객으로 상정하는 인상을 준다.
 * enum 값 자체는 남겨 둔다 — 후기 Dict 13종에 이미 존재하고, BO 통계·후기 표기가 참조한다.
 */
export const MEMBER_ROLE_OPTIONS: CardOption[] = [
  { value: 'STUDENT', label: '학생' },
  { value: 'PARENT', label: '학부모' },
  { value: 'TEACHER', label: '교사' },
  { value: 'COUNSELOR', label: '진학상담교사' },
  { value: 'INSTRUCTOR', label: '학원 강사' },
  { value: 'ETC', label: '기타' },
];

/**
 * 학년 4종 — 입시카드 {@link GRADE_OPTIONS} 를 **그대로 재수출**한다(2026-08-13 통합).
 *
 * 가입에서 넣은 학년이 입시카드로 프리필되는 구조라(설계 §5.1) 두 화면의 값 집합이 어긋나면
 * 프리필이 조용히 깨진다. 이전에는 같은 배열을 두 파일에 각각 두고 주석으로 "동일해야 한다"고
 * 방어했으나, 한쪽만 고치면 막을 방법이 없어 정의를 하나로 합쳤다.
 *
 * 입시카드 쪽을 원본으로 둔 이유: `member_profile.grade`(C층)가 V949 정규화의 대상이었고,
 * 이 파일이 이미 그쪽 `CardOption` 타입에 의존한다 — 의존 방향이 자연스럽다.
 *
 * 이름을 `STUDENT_GRADE_OPTIONS` 로 유지하는 것은 B층 컬럼명(`student_grade`)과 맞추기 위해서다.
 */
export { GRADE_OPTIONS as STUDENT_GRADE_OPTIONS } from '#/data/analysis/ipsiCardOptions';

/** 시도 17종 — 후기 Dict `review_author_region`·`highschool.sido` 와 동일 코드체계. */
export const REGION_OPTIONS: CardOption[] = [
  { value: 'SEOUL', label: '서울' },
  { value: 'BUSAN', label: '부산' },
  { value: 'DAEGU', label: '대구' },
  { value: 'INCHEON', label: '인천' },
  { value: 'GWANGJU', label: '광주' },
  { value: 'DAEJEON', label: '대전' },
  { value: 'ULSAN', label: '울산' },
  { value: 'SEJONG', label: '세종' },
  { value: 'GYEONGGI', label: '경기' },
  { value: 'GANGWON', label: '강원' },
  { value: 'CHUNGBUK', label: '충북' },
  { value: 'CHUNGNAM', label: '충남' },
  { value: 'JEONBUK', label: '전북' },
  { value: 'JEONNAM', label: '전남' },
  { value: 'GYEONGBUK', label: '경북' },
  { value: 'GYEONGNAM', label: '경남' },
  { value: 'JEJU', label: '제주' },
];

/**
 * 학년을 노출하는 역할 — 학생·학부모만.
 * 학생은 본인 학년, 학부모는 자녀 학년이다. 교사에게는 "내 학년"이 성립하지 않는다.
 */
const ROLES_WITH_GRADE = ['STUDENT', 'PARENT'] as const;

/**
 * 학교를 노출하는 역할 — 학생·학부모(재학) + 교사·진학상담교사(재직).
 *
 * 진학상담교사(`COUNSELOR`)는 고등학교에 배치된 진로진학상담교사라 **재직 학교가 있고**,
 * 그 학교가 고교 마스터에 존재한다. 교사와 같은 취급이 맞다(2026-08-13 결정).
 *
 * ⚠️ **학원 강사(`INSTRUCTOR`)는 제외**한다 — 소속이 학원이라 고교 마스터에 없다.
 * 노출해 봐야 전부 `school_etc`(자유 텍스트)로 빠져 세그먼트로서 가치가 없고,
 * "찾아도 안 나오는" 경험만 남는다. `ETC` 도 대상을 특정할 수 없어 같은 이유로 제외.
 */
const ROLES_WITH_SCHOOL = ['STUDENT', 'PARENT', 'TEACHER', 'COUNSELOR'] as const;

/** 해당 역할에서 학년 입력을 보여줄지. 역할 미선택(빈 값)이면 감춘다. */
export function showsGradeField(role: string): boolean {
  return (ROLES_WITH_GRADE as readonly string[]).includes(role);
}

/** 해당 역할에서 학교 입력을 보여줄지. 역할 미선택(빈 값)이면 감춘다. */
export function showsSchoolField(role: string): boolean {
  return (ROLES_WITH_SCHOOL as readonly string[]).includes(role);
}

/**
 * 학년 라벨 — 학부모는 "자녀 학년"이다(주체가 다르다 — 설계 §3.2).
 * 이 라벨 차이가 입시카드 프리필에서 학부모를 제외하는 근거이기도 하다(§5.1).
 */
export function gradeFieldLabel(role: string): string {
  return role === 'PARENT' ? '자녀 학년' : '학년';
}

/** 학교 라벨 — 교사·진학상담교사는 재직 학교라 의미가 다르다. */
export function schoolFieldLabel(role: string): string {
  return role === 'TEACHER' || role === 'COUNSELOR' ? '재직 학교' : '학교';
}
